<template>
  <b-modal
    v-model="isModalActive"
    :width="900"
    scroll="keep"
    :can-cancel="['escape', 'x']"
    @close="closeModal"
  >
    <div class="modal-card" style="width: auto">
      <header class="modal-card-head">
        <p class="modal-card-title" id="fps-modal-title">{{ labels.selectPaymentConcepts }}</p>
        <button
          type="button"
          class="delete"
          aria-label="Cerrar modal"
          @click="closeModal"
        ></button>
      </header>

      <section class="modal-card-body" aria-labelledby="fps-modal-title">

        <!-- Progress Steps -->
        <nav class="fps-steps-container mb-5" aria-label="Pasos del proceso">
          <ol class="fps-steps">
            <li
              v-for="(step, index) in steps"
              :key="index"
              class="fps-step-item"
              :class="{
                'is-active': currentStep === index + 1,
                'is-completed': currentStep > index + 1
              }"
              :aria-current="currentStep === index + 1 ? 'step' : undefined"
              :aria-label="`Paso ${index + 1}: ${step}${currentStep > index + 1 ? ', completado' : currentStep === index + 1 ? ', actual' : ''}`"
            >
              <div class="fps-step-marker" aria-hidden="true">
                <span v-if="currentStep > index + 1">✓</span>
                <span v-else>{{ index + 1 }}</span>
              </div>
              <div class="fps-step-label" aria-hidden="true">{{ step }}</div>
            </li>
          </ol>
        </nav>

        <!-- Step 1: Conceptos de Pago -->
        <div v-if="currentStep === 1" role="region" aria-label="Selección de conceptos de pago">

          <div v-if="loadingProducts" class="fps-loading" role="status" aria-live="polite">
            <b-loading :is-full-page="false" v-model="loadingProducts"></b-loading>
            <p>{{ labels.loadingProducts }}</p>
          </div>

          <b-message
            type="is-danger"
            v-if="errorMessage"
            class="mb-4"
            role="alert"
          >
            {{ errorMessage }}
          </b-message>

          <div class="fps-table-wrapper" v-if="!loadingProducts && !errorMessage">
            <table
              class="table is-fullwidth is-hoverable fps-concepts-table"
              aria-label="Conceptos de pago disponibles"
            >
              <thead>
                <tr>
                  <th scope="col">
                    <b-checkbox
                      v-model="selectAll"
                      @input="toggleSelectAll"
                      :aria-label="`${selectAll ? 'Deseleccionar' : 'Seleccionar'} todos los conceptos`"
                    ></b-checkbox>
                  </th>
                  <th scope="col">{{ labels.concept }}</th>
                  <th scope="col">{{ labels.description }}</th>
                  <th scope="col">{{ labels.amount }}</th>
                  <th scope="col">{{ labels.status }}</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="concept in paymentConcepts"
                  :key="concept.paymentId || concept.productKey"
                  :class="{
                    'fps-row-disabled': concept.disabled,
                    'fps-row-selected': concept.selected && !concept.disabled
                  }"
                >
                  <td>
                    <b-checkbox
                      v-model="concept.selected"
                      :disabled="concept.disabled"
                      @input="updateSelection"
                      :aria-label="`Seleccionar ${concept.name}`"
                    ></b-checkbox>
                  </td>
                  <td>
                    <span class="fps-concept-name">{{ concept.name }}</span>
                    <span
                      v-if="concept.disabled && concept.isPaid"
                      class="fps-paid-badge"
                      title="Concepto completamente pagado"
                    >
                      <i class="fas fa-lock" aria-hidden="true"></i>
                      <span class="fps-sr-only">Pagado</span>
                    </span>
                  </td>
                  <td>
                    <span class="fps-text-secondary">{{ concept.description }}</span>
                  </td>
                  <td>
                    <div v-if="concept.selected && !concept.disabled" class="fps-amount-field">
                      <b-input
                        v-model.number="concept.amount"
                        type="number"
                        step="0.01"
                        min="0"
                        :max="getAvailableAmountForConcept(concept.productKey, concept)"
                        @input="validateConceptAmount(concept)"
                        size="is-small"
                        :class="{ 'is-danger': isAmountInvalid(concept) }"
                        :aria-label="`Monto para ${concept.name}`"
                        :aria-describedby="`fps-hint-${concept.paymentId || concept.productKey}`"
                        :aria-invalid="isAmountInvalid(concept) ? 'true' : 'false'"
                      ></b-input>
                      <p
                        :id="`fps-hint-${concept.paymentId || concept.productKey}`"
                        class="fps-amount-hint"
                        :class="isAmountInvalid(concept) ? 'fps-hint-error' : 'fps-hint-neutral'"
                      >
                        Máx. {{ formatCurrency(getAvailableAmountForConcept(concept.productKey, concept)) }}
                        <span v-if="isAmountInvalid(concept)"> · Fuera de rango</span>
                      </p>
                    </div>
                    <span v-else class="fps-amount-display">
                      {{ formatCurrency(concept.amount) }}
                    </span>
                  </td>
                  <td>
                    <b-tag
                      :type="concept.isPaid ? 'is-success' : (concept.isExistingPayment ? 'is-info' : 'is-warning')"
                      size="is-small"
                    >
                      {{ concept.isPaid ? labels.paid : (concept.isExistingPayment ? (i18n.commitment && i18n.commitment[iso] || 'Compromiso') : labels.pending) }}
                    </b-tag>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <!-- Resumen de selección -->
          <div
            v-if="!loadingProducts && !errorMessage"
            class="fps-selection-summary"
            role="status"
            aria-live="polite"
            aria-atomic="true"
          >
            <div class="fps-summary-item">
              <span class="fps-summary-label">{{ labels.selectedConcepts }}</span>
              <span class="fps-summary-value">{{ selectedConceptsCount }}</span>
            </div>
            <div class="fps-summary-divider" aria-hidden="true"></div>
            <div class="fps-summary-item">
              <span class="fps-summary-label">{{ labels.totalToPay }}</span>
              <span class="fps-summary-value fps-summary-total">{{ formatCurrency(totalToPay) }}</span>
            </div>
          </div>

          <!-- Alerta de montos inválidos -->
          <div
            v-if="!canProceedToNextStep && selectedConceptsCount > 0 && !areAllAmountsValid"
            class="fps-amounts-warning"
            role="alert"
          >
            <i class="fas fa-exclamation-triangle" aria-hidden="true"></i>
            Corrige los montos inválidos para continuar.
          </div>

          <!-- Suscripción Mensual -->
          <div class="fps-subscription mt-4" v-if="!loadingProducts && !errorMessage">
            <div class="fps-subscription-header">
              <i class="fas fa-calendar-alt" aria-hidden="true"></i>
              <span>{{ labels.monthlySubscription }}</span>
            </div>
            <!-- Stats row -->
            <div class="fps-sub-stats" role="group" :aria-label="labels.monthlySubscription">
              <div class="fps-stat-item">
                <span class="fps-stat-label">{{ labels.remainingBalance }}</span>
                <span class="fps-stat-value">{{ formatCurrency(detailsContract.Saldo) }}</span>
              </div>
              <div class="fps-stat-sep" aria-hidden="true"></div>
              <div class="fps-stat-item">
                <span class="fps-stat-label">{{ labels.monthsToFinance }}</span>
                <span class="fps-stat-value">{{ monthsToFinance }}</span>
              </div>
              <div class="fps-stat-sep" aria-hidden="true"></div>
              <div class="fps-stat-item">
                <span class="fps-stat-label">{{ labels.monthlyPayment }}</span>
                <span class="fps-stat-value fps-stat-accent">{{ formatCurrency(monthlyPayment) }}</span>
              </div>
            </div>
            <!-- Action row — full width so el datepicker tiene espacio -->
            <div class="fps-sub-action-row">
              <template v-if="selectedConceptsCount === 0">
                <b-field :label="labels.subscriptionStartDate" custom-class="is-small fps-label-compact" class="fps-field-tight">
                  <b-datepicker
                    v-model="subscriptionStartDate"
                    icon="calendar-today"
                    icon-pack="fas"
                    size="is-small"
                    :min-date="new Date()"
                    placeholder="Seleccionar fecha"
                    append-to-body
                    position="is-top-right"
                  ></b-datepicker>
                </b-field>
                <b-button
                  type="is-primary"
                  icon-left="calendar-check"
                  icon-pack="fas"
                  @click="createSubscription"
                  :loading="creatingSubscription"
                  :disabled="!isValidForSubscription || !subscriptionStartDate"
                  size="is-small"
                >
                  {{ labels.createSubscription }}
                </b-button>
                <p
                  v-if="!isValidForSubscription && subscriptionDisabledReason"
                  class="fps-reason"
                  role="note"
                >
                  {{ subscriptionDisabledReason }}
                </p>
              </template>
              <template v-else>
                <b-field class="fps-field-tight">
                  <b-switch v-model="includeSubscription" size="is-small">
                    {{ labels.activateSubscription }}
                  </b-switch>
                </b-field>
                <template v-if="includeSubscription">
                  <b-field :label="labels.subscriptionStartDate" custom-class="is-small fps-label-compact" class="fps-field-tight">
                    <b-datepicker
                      v-model="subscriptionStartDate"
                      icon="calendar-today"
                      icon-pack="fas"
                      size="is-small"
                      :min-date="new Date()"
                      placeholder="Seleccionar fecha"
                      append-to-body
                      position="is-top-right"
                    ></b-datepicker>
                  </b-field>
                  <p class="fps-sub-note">{{ labels.subscriptionStartsNote }}</p>
                </template>
              </template>
            </div>
          </div>
        </div>

        <!-- Step 2: Selección de Contactos -->
        <div v-if="currentStep === 2" role="region" aria-label="Selección de contactos para envío">

          <div class="fps-members-selector mb-4">
            <p class="fps-section-title">{{ labels.contractMembers }}</p>
            <div class="field has-addons">
              <div class="control is-expanded">
                <div class="select is-fullwidth">
                  <select v-model="selectedMemberId" :aria-label="labels.selectMember">
                    <option value="">{{ labels.selectMember }}</option>
                    <option
                      v-for="member in allContractMembers"
                      :key="member._id"
                      :value="member._id"
                    >
                      {{ member.Nombre }} {{ member.ApellidoPaterno }} - {{ member.Email }}
                    </option>
                  </select>
                </div>
              </div>
              <div class="control">
                <b-button
                  type="is-primary"
                  icon-left="plus"
                  @click="addSelectedMember"
                  :disabled="!selectedMemberId"
                >
                  {{ labels.addMember }}
                </b-button>
              </div>
            </div>
          </div>

          <div class="fps-contacts-grid">
            <!-- Emails -->
            <section class="fps-contact-group" aria-label="Correos electrónicos">
              <h6 class="fps-contact-group-title">
                <i class="fas fa-envelope" aria-hidden="true"></i>
                {{ labels.emails }}
              </h6>
              <div class="fps-contact-group-body">
                <div class="field has-addons mb-3">
                  <div class="control is-expanded">
                    <input
                      class="input"
                      type="email"
                      v-model="newEmail"
                      :placeholder="labels.enterEmail"
                      @keyup.enter="addManualEmail"
                      aria-label="Correo electrónico a agregar"
                    />
                  </div>
                  <div class="control">
                    <b-button
                      type="is-primary"
                      @click="addManualEmail"
                      :disabled="!newEmail || !isValidEmail(newEmail)"
                    >
                      {{ labels.add }}
                    </b-button>
                  </div>
                </div>
                <ul
                  class="fps-chip-list"
                  role="list"
                  aria-live="polite"
                  aria-label="Correos agregados"
                >
                  <li
                    v-for="(email, index) in selectedContacts.emails"
                    :key="`email-${index}`"
                    class="fps-chip"
                  >
                    <i class="fas fa-envelope fps-chip-icon" aria-hidden="true"></i>
                    <span class="fps-chip-text">{{ email }}</span>
                    <button
                      class="fps-chip-remove"
                      type="button"
                      @click="removeEmail(index)"
                      :aria-label="`Eliminar correo ${email}`"
                    >
                      <i class="fas fa-times" aria-hidden="true"></i>
                    </button>
                  </li>
                  <li v-if="selectedContacts.emails.length === 0" class="fps-chip-empty">
                    {{ labels.noEmails }}
                  </li>
                </ul>
              </div>
            </section>

            <!-- Teléfonos -->
            <section class="fps-contact-group" aria-label="Teléfonos">
              <h6 class="fps-contact-group-title">
                <i class="fas fa-phone" aria-hidden="true"></i>
                {{ labels.phones }}
              </h6>
              <div class="fps-contact-group-body">
                <div class="field has-addons mb-3">
                  <div class="control is-expanded">
                    <input
                      class="input"
                      type="tel"
                      v-model="newPhone"
                      :placeholder="labels.enterPhone"
                      @keyup.enter="addManualPhone"
                      aria-label="Teléfono a agregar"
                    />
                  </div>
                  <div class="control">
                    <b-button
                      type="is-primary"
                      @click="addManualPhone"
                      :disabled="!newPhone"
                    >
                      {{ labels.add }}
                    </b-button>
                  </div>
                </div>
                <ul
                  class="fps-chip-list"
                  role="list"
                  aria-live="polite"
                  aria-label="Teléfonos agregados"
                >
                  <li
                    v-for="(phone, index) in selectedContacts.phones"
                    :key="`phone-${index}`"
                    class="fps-chip"
                  >
                    <i class="fas fa-phone fps-chip-icon" aria-hidden="true"></i>
                    <span class="fps-chip-text">{{ phone }}</span>
                    <button
                      class="fps-chip-remove"
                      type="button"
                      @click="removePhone(index)"
                      :aria-label="`Eliminar teléfono ${phone}`"
                    >
                      <i class="fas fa-times" aria-hidden="true"></i>
                    </button>
                  </li>
                  <li v-if="selectedContacts.phones.length === 0" class="fps-chip-empty">
                    {{ labels.noPhones }}
                  </li>
                </ul>
              </div>
            </section>
          </div>
        </div>

        <!-- Step 3: Revisión Final -->
        <div v-if="currentStep === 3" role="region" aria-label="Revisión final antes de enviar">

          <div class="fps-review-section mb-4">
            <p class="fps-review-section-title">{{ labels.selectedConcepts }}</p>
            <table
              class="table is-fullwidth fps-review-table"
              aria-label="Resumen de conceptos seleccionados"
            >
              <thead>
                <tr>
                  <th scope="col">{{ labels.concept }}</th>
                  <th scope="col" class="fps-amount-col">{{ labels.amount }}</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="concept in selectedPaymentConcepts" :key="concept.productKey">
                  <td>{{ concept.name }}</td>
                  <td class="fps-amount-col">{{ formatCurrency(concept.amount) }}</td>
                </tr>
              </tbody>
              <tfoot>
                <tr class="fps-total-row">
                  <th scope="row">{{ labels.total }}</th>
                  <td class="fps-amount-col fps-total-cell">{{ formatCurrency(totalToPay) }}</td>
                </tr>
              </tfoot>
            </table>
          </div>

          <div class="fps-contacts-grid">
            <section class="fps-contact-group" aria-label="Correos electrónicos seleccionados">
              <h6 class="fps-contact-group-title">
                <i class="fas fa-envelope" aria-hidden="true"></i>
                {{ labels.emails }}
                <span class="fps-count-badge">{{ selectedContacts.emails.length }}</span>
              </h6>
              <ul class="fps-chip-list fps-chip-list-readonly" role="list">
                <li
                  v-for="(email, index) in selectedContacts.emails"
                  :key="`rev-email-${index}`"
                  class="fps-chip fps-chip-readonly"
                >
                  <i class="fas fa-envelope fps-chip-icon" aria-hidden="true"></i>
                  <span class="fps-chip-text">{{ email }}</span>
                </li>
                <li v-if="selectedContacts.emails.length === 0" class="fps-chip-empty">
                  {{ labels.noEmails }}
                </li>
              </ul>
            </section>

            <section class="fps-contact-group" aria-label="Teléfonos seleccionados">
              <h6 class="fps-contact-group-title">
                <i class="fas fa-phone" aria-hidden="true"></i>
                {{ labels.phones }}
                <span class="fps-count-badge">{{ selectedContacts.phones.length }}</span>
              </h6>
              <ul class="fps-chip-list fps-chip-list-readonly" role="list">
                <li
                  v-for="(phone, index) in selectedContacts.phones"
                  :key="`rev-phone-${index}`"
                  class="fps-chip fps-chip-readonly"
                >
                  <i class="fas fa-phone fps-chip-icon" aria-hidden="true"></i>
                  <span class="fps-chip-text">{{ phone }}</span>
                </li>
                <li v-if="selectedContacts.phones.length === 0" class="fps-chip-empty">
                  {{ labels.noPhones }}
                </li>
              </ul>
            </section>
          </div>
        </div>

      </section>

      <footer class="modal-card-foot">
        <div class="fps-footer">
          <div class="fps-footer-left">
            <b-button @click="closeModal">{{ labels.cancel }}</b-button>
          </div>
          <div class="fps-footer-right">
            <b-button
              v-if="currentStep > 1"
              @click="previousStep"
              icon-left="arrow-left"
              icon-pack="fas"
            >
              {{ labels.previous }}
            </b-button>
            <b-button
              v-if="currentStep < 3"
              type="is-primary"
              @click="nextStep"
              :disabled="!canProceedToNextStep"
              icon-right="arrow-right"
              icon-pack="fas"
            >
              {{ labels.next }}
            </b-button>
            <b-button
              v-if="currentStep === 3"
              type="is-success"
              @click="confirmSelection"
              :disabled="!isValidForFinalSubmission"
              :loading="creatingCheckoutSession"
              icon-left="paper-plane"
              icon-pack="fas"
            >
              {{ labels.proceedToPayment }}
            </b-button>
          </div>
        </div>
      </footer>
    </div>
  </b-modal>
</template>

<script>
import axios from 'axios'

const API_URL = process.env.VUE_APP_API_URL

export default {
  name: 'FinancingPaymentSelectorNew',
  props: {
    isActive: {
      type: Boolean,
      default: false
    },
    contractInfo: {
      type: Object,
      required: true
    },
    detailsContract: {
      type: Object,
      required: true
    },
    totalPaidAmount: {
      type: Number,
      default: 0
    },
    totalContractValue: {
      type: Number,
      default: 0
    },
    remainingBalance: {
      type: Number,
      default: 0
    },
    additionalPayments: {
      type: Array,
      default: () => []
    },
    conditionalDownPayment: {
      type: Array,
      default: () => []
    },
    parseIntNumberDecimals: {
      type: Function,
      required: true
    },
    i18n: {
      type: Object,
      required: true
    },
    iso: {
      type: String,
      default: 'es'
    },
    allContractMembers: {
      type: Array,
      default: () => []
    },
    conceptProgress: {
      type: Object,
      default: () => ({
        downPayment: { paid: 0, total: 0, percentage: 0 },
        adminCost: { paid: 0, total: 0, percentage: 0 },
        additionalPayment: { paid: 0, total: 0, percentage: 0 }
      })
    }
  },
  data() {
    return {
      isModalActive: false,
      currentStep: 1,
      selectAll: false,
      paymentConcepts: [],
      loadingProducts: false,
      errorMessage: '',
      creatingCheckoutSession: false,
      creatingSubscription: false,
      downPaymentPaid: false,
      selectedMemberId: '',
      newEmail: '',
      newPhone: '',
      selectedContacts: {
        emails: [],
        phones: []
      },
      showTooltip: false,
      subscriptionStartDate: null,
      includeSubscription: false
    }
  },
  computed: {
    steps() {
      return [
        this.labels.conceptsStep || 'Conceptos',
        this.labels.contactsStep || 'Contactos',
        this.labels.reviewStep || 'Revisión'
      ]
    },
    selectedConceptsCount() {
      return this.paymentConcepts.filter(concept => concept.selected && !concept.disabled).length
    },
    totalToPay() {
      let total = 0

      this.paymentConcepts.forEach(concept => {
        if (concept.selected && !concept.disabled) {
          total += parseFloat(concept.amount) || 0
        }
      })

      return total
    },
    hasAdditionalPaymentSelected() {
      const existingAdditionalPayments = this.paymentConcepts.filter(c => c.isExistingPayment)
      return existingAdditionalPayments.some(payment => payment.selected)
    },
    isAdditionalPaymentValid() {
      const existingAdditionalPayments = this.paymentConcepts.filter(c => c.isExistingPayment && c.selected)
      if (existingAdditionalPayments.length === 0) return true

      return existingAdditionalPayments.every(payment => payment.amount > 0)
    },
    areAllAmountsValid() {
      const selectedConcepts = this.paymentConcepts.filter(concept => concept.selected && !concept.disabled)

      return selectedConcepts.every(concept => {
        // Verificar que el monto sea mayor a 0
        if (concept.amount <= 0) return false

        // Verificar que no exceda el máximo permitido
        const maxAmount = this.getAvailableAmountForConcept(concept.productKey)
        if (maxAmount !== null && concept.amount > maxAmount) return false

        return true
      })
    },
    invalidConcepts() {
      const selectedConcepts = this.paymentConcepts.filter(concept => concept.selected && !concept.disabled)

      return selectedConcepts.filter(concept => {
        // Verificar que el monto sea mayor a 0
        if (concept.amount <= 0) return true

        // Verificar que no exceda el máximo permitido
        const maxAmount = this.getAvailableAmountForConcept(concept.productKey)
        if (maxAmount !== null && concept.amount > maxAmount) return true

        return false
      })
    },
    canProceedToNextStep() {
      if (this.currentStep === 1) {
        const hasSelectedConcepts = this.selectedConceptsCount > 0
        const additionalPaymentValid = this.isAdditionalPaymentValid
        const amountsValid = this.areAllAmountsValid
        return hasSelectedConcepts && additionalPaymentValid && amountsValid
      } else if (this.currentStep === 2) {
        return this.selectedContacts.emails.length > 0 || this.selectedContacts.phones.length > 0
      }
      return true
    },
    isValidForFinalSubmission() {
      return this.selectedConceptsCount > 0 &&
             this.isAdditionalPaymentValid &&
             this.areAllAmountsValid &&
             (this.selectedContacts.emails.length > 0 || this.selectedContacts.phones.length > 0) &&
             !this.creatingCheckoutSession
    },
    monthsToFinance() {
      return this.detailsContract?.Mensualidades || 24
    },
    monthlyPayment() {
      const saldoToFinance = this.detailsContract?.Saldo || 0
      if (saldoToFinance <= 0 || this.monthsToFinance <= 0) return 0
      return saldoToFinance / this.monthsToFinance
    },
    isValidForSubscription() {
      const saldoToFinance = this.detailsContract?.Saldo || 0
      const hasValidSaldo = saldoToFinance > 0 && this.monthsToFinance > 0

      const downPaymentAmount = this.detailsContract?.PagoInicial || 0
      const downPaymentRequired = downPaymentAmount > 0

      if (downPaymentRequired && !this.downPaymentPaid) {
        return false
      }

      return hasValidSaldo
    },
    subscriptionDisabledReason() {
      const saldoToFinance = this.detailsContract?.Saldo || 0
      const downPaymentAmount = this.detailsContract?.PagoInicial || 0
      const downPaymentRequired = downPaymentAmount > 0

      if (saldoToFinance <= 0) {
        return this.iso === 'en' ? 'No remaining balance to finance' : 'No hay saldo pendiente para financiar'
      }

      if (this.monthsToFinance <= 0) {
        return this.iso === 'en' ? 'No installments configured' : 'No hay mensualidades configuradas'
      }

      if (downPaymentRequired && !this.downPaymentPaid) {
        return this.iso === 'en' ? 'Down payment must be completed first' : 'Debe completar el pago inicial primero'
      }

      return ''
    },
    currentLanguage() {
      return this.iso === 'en' ? 'english' : 'spanish'
    },
    selectedPaymentConcepts() {
      return this.paymentConcepts
        .filter(concept => concept.selected && !concept.disabled)
        .map(concept => ({
          productKey: concept.productKey,
          name: concept.name,
          amount: concept.amount,
          isExistingPayment: concept.isExistingPayment || false,
          paymentId: concept.paymentId || null
        }))
    },
    labels() {
      const defaultLabels = {
        selectPaymentConcepts: { es: 'Seleccionar Conceptos de Pago', en: 'Select Payment Concepts' },
        contractInfo: { es: 'Información del Contrato', en: 'Contract Information' },
        contractNumber: { es: 'Número de Contrato', en: 'Contract Number' },
        memberName: { es: 'Nombre del Miembro', en: 'Member Name' },
        contractPrice: { es: 'Precio del Contrato', en: 'Contract Price' },
        totalPaid: { es: 'Total Pagado', en: 'Total Paid' },
        loadingProducts: { es: 'Cargando productos...', en: 'Loading products...' },
        concept: { es: 'Concepto', en: 'Concept' },
        description: { es: 'Descripción', en: 'Description' },
        amount: { es: 'Monto', en: 'Amount' },
        status: { es: 'Estado', en: 'Status' },
        paid: { es: 'Pagado', en: 'Paid' },
        pending: { es: 'Pendiente', en: 'Pending' },
        selectedConcepts: { es: 'Conceptos Seleccionados', en: 'Selected Concepts' },
        totalToPay: { es: 'Total a Pagar', en: 'Total to Pay' },
        additionalPaymentRequired: { es: 'Debe ingresar un monto válido para el pago adicional', en: 'You must enter a valid amount for the additional payment' },
        cancel: { es: 'Cancelar', en: 'Cancel' },
        next: { es: 'Siguiente', en: 'Next' },
        previous: { es: 'Anterior', en: 'Previous' },
        proceedToPayment: { es: 'Enviar Solicitud de Pago', en: 'Send Payment Request' },
        monthlySubscription: { es: 'Suscripción Mensual', en: 'Monthly Subscription' },
        remainingBalance: { es: 'Saldo', en: 'Balance' },
        monthsToFinance: { es: 'Mensualidades', en: 'Installments' },
        monthlyPayment: { es: 'Pago Mensual', en: 'Monthly Payment' },
        createSubscription: { es: 'Crear Suscripción', en: 'Create Subscription' },
        contactSelection: { es: 'Selección de Contactos', en: 'Contact Selection' },
        contactSelectionDescription: { es: 'Selecciona los miembros del contrato o agrega contactos manualmente para enviar el enlace de pago', en: 'Select the contract members or manually add contacts to send the payment link' },
        contractMembers: { es: 'Miembros del Contrato', en: 'Contract Members' },
        selectMember: { es: 'Seleccionar miembro', en: 'Select member' },
        addMember: { es: 'Agregar', en: 'Add' },
        emails: { es: 'Correos Electrónicos', en: 'Emails' },
        phones: { es: 'Teléfonos', en: 'Phones' },
        enterEmail: { es: 'Ingresar correo', en: 'Enter email' },
        enterPhone: { es: 'Ingresar teléfono', en: 'Enter phone' },
        add: { es: 'Agregar', en: 'Add' },
        noEmails: { es: 'No hay correos agregados', en: 'No emails added' },
        noPhones: { es: 'No hay teléfonos agregados', en: 'No phones added' },
        reviewTitle: { es: 'Revisión Final', en: 'Final Review' },
        reviewDescription: { es: 'Revisa la información antes de enviar el enlace de pago', en: 'Review the information before sending the payment link' },
        total: { es: 'Total', en: 'Total' },
        conceptsStep: { es: 'Conceptos', en: 'Concepts' },
        contactsStep: { es: 'Contactos', en: 'Contacts' },
        reviewStep: { es: 'Revisión', en: 'Review' },
        activateSubscription: { es: 'Activar domiciliación mensual', en: 'Activate monthly direct debit' },
        subscriptionStartDate: { es: 'Fecha de inicio', en: 'Start date' },
        subscriptionStartsNote: { es: 'La domiciliación inicia en la fecha elegida.', en: 'The subscription starts on the selected date.' }
      }

      const lang = this.iso === 'en' ? 'en' : 'es'

      if (!this.i18n) {
        const fallback = {}
        Object.keys(defaultLabels).forEach(key => {
          fallback[key] = defaultLabels[key][lang]
        })
        return fallback
      }

      const result = {}
      Object.keys(defaultLabels).forEach(key => {
        result[key] = (this.i18n[key] && this.i18n[key][this.iso]) || defaultLabels[key][lang]
      })

      return result
    }
  },
  watch: {
    isActive(newVal) {
      console.log('FinancingPaymentSelectorNew - isActive changed:', newVal)
      this.isModalActive = newVal
      if (newVal) {
        console.log('Loading financing products...')
        this.currentStep = 1
        this.resetContacts()
        this.loadConceptProgress().then(() => {
          this.loadFinancingProducts()
        })
      }
    },
    isModalActive(newVal) {
      if (!newVal) {
        this.$emit('close')
      }
    }
  },
  methods: {
    formatCurrency(value) {
      if (!value && value !== 0) return '$0.00'
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD'
      }).format(value)
    },

    async loadFinancingProducts() {
      console.log('loadFinancingProducts called')
      console.log('contractInfo:', this.contractInfo)
      console.log('detailsContract:', this.detailsContract)

      this.loadingProducts = true
      this.errorMessage = ''

      try {
        const response = await axios.get(`${API_URL}/financing/products`, {
          params: {
            language: this.currentLanguage
          }
        })

        if (response.data.success) {
          await this.initializePaymentConcepts(response.data.data)
        } else {
          this.errorMessage = response.data.error || 'Error cargando productos'
        }
      } catch (error) {
        console.error('Error cargando productos de financiamiento:', error)
        this.errorMessage = (error.response && error.response.data && error.response.data.error) || 'Error de conexión al cargar productos'
      } finally {
        this.loadingProducts = false
      }
    },

    async initializePaymentConcepts(products) {
      console.log('initializePaymentConcepts called with products:', products)
      console.log('additionalPayments:', this.additionalPayments)
      console.log('conditionalDownPayment:', this.conditionalDownPayment)

      // Crear conceptos base de productos (excluyendo additionalPayment genérico)
      const baseConceptsPromises = products
        .filter(product => product.productKey !== 'additionalPayment') // Excluir additionalPayment genérico
        .map(async product => {
          let amount = 0
          let disabled = false
          let isPaid = false

          switch (product.productKey) {
            case 'downPayment':
              amount = this.detailsContract.PagoInicial || 0
              isPaid = await this.checkIfConceptPaid('downPayment')
              this.downPaymentPaid = isPaid
              break
            case 'adminCost':
              amount = this.detailsContract.CostoAdministracion || 0
              isPaid = await this.checkIfConceptPaid('adminCost')
              break
          }

          // Calcular el monto disponible para pagar (no el total del contrato)
          const availableAmount = this.getAvailableAmountForConcept(product.productKey, null)
          amount = availableAmount > 0 ? availableAmount : 0

          // Para conceptos base, usar el monto total del contrato
          // El monto disponible se calculará dinámicamente cuando se seleccione el concepto

          // Solo deshabilitar si está completamente pagado (100%) o no hay monto disponible
          if (amount === 0 || isPaid) {
            disabled = true
          }

          // Si hay progreso parcial pero no está completamente pagado, permitir selección
          if (this.conceptProgress && this.conceptProgress[product.productKey]) {
            const progress = this.conceptProgress[product.productKey]
            if (progress.percentage > 0 && progress.percentage < 100) {
              disabled = false // Permitir selección para pagos parciales
            }
          }

          return {
            productKey: product.productKey,
            stripeProductId: product.stripeProductId,
            name: product.name,
            description: product.description,
            amount: amount,
            selected: false,
            disabled: disabled,
            isPaid: isPaid,
            config: product.config,
            isExistingPayment: false,
            paymentId: product.productKey
          }
        })

      let baseConcepts = await Promise.all(baseConceptsPromises)
      baseConcepts = baseConcepts.filter(concept =>
        concept.amount > 0 && concept.productKey !== 'additionalPayment'
      )

      // Agregar pagos adicionales existentes
      let existingAdditionalPayments = []

      // Procesar additionalPayments
      if (this.additionalPayments && this.additionalPayments.length > 0) {
        this.additionalPayments.forEach((payment, index) => {
          const amount = typeof payment.CargoPago === 'string'
            ? this.parseIntNumberDecimals(payment.CargoPago)
            : (parseFloat(payment.CargoPago) || 0)

          if (amount > 0) {
            // Usar el progreso calculado en lugar del estado del contrato
            const productKey = 'additionalPayment'
            const conceptProgress = this.conceptProgress[productKey]
            const isPaid = conceptProgress && conceptProgress.percentage >= 100

            // Buscar el producto additionalPayment para obtener el stripeProductId
            const additionalPaymentProduct = products.find(p => p.productKey === 'additionalPayment')

            existingAdditionalPayments.push({
              productKey: productKey,
              stripeProductId: additionalPaymentProduct?.stripeProductId || '',
              name: `${(this.i18n.additionalPayment && this.i18n.additionalPayment[this.iso]) || 'Pago Adicional'} ${index + 1}`,
              description: `${(this.i18n.additionalPayment && this.i18n.additionalPayment[this.iso]) || 'Pago Adicional'} - ${payment.FechaPagoDate || ''}`,
              amount: amount,
              selected: false,
              disabled: isPaid,
              isPaid: isPaid,
              config: { allowCustomAmount: false },
              isExistingPayment: true,
              originalPayment: payment,
              paymentId: payment._id
            })
          }
        })
      }

      // Procesar conditionalDownPayment
      if (this.conditionalDownPayment && this.conditionalDownPayment.length > 0) {
        this.conditionalDownPayment.forEach((payment, index) => {
          const amount = typeof payment.CargoPago === 'string'
            ? this.parseIntNumberDecimals(payment.CargoPago)
            : (parseFloat(payment.CargoPago) || 0)

          if (amount > 0) {
            // Usar el progreso calculado en lugar del estado del contrato
            const productKey = 'additionalPayment'
            const conceptProgress = this.conceptProgress[productKey]
            const isPaid = conceptProgress && conceptProgress.percentage >= 100

            // Buscar el producto additionalPayment para obtener el stripeProductId
            const additionalPaymentProduct = products.find(p => p.productKey === 'additionalPayment')

            existingAdditionalPayments.push({
              productKey: productKey,
              stripeProductId: additionalPaymentProduct?.stripeProductId || '',
              name: `${(this.i18n.closingCost && this.i18n.closingCost[this.iso]) || 'Costo de Cierre'} ${index + 1}`,
              description: `${(this.i18n.closingCost && this.i18n.closingCost[this.iso]) || 'Costo de Cierre'} - ${payment.FechaPagoDate || ''}`,
              amount: amount,
              selected: false,
              disabled: isPaid,
              isPaid: isPaid,
              config: { allowCustomAmount: false },
              isExistingPayment: true,
              originalPayment: payment,
              paymentId: payment._id
            })
          }
        })
      }

      this.paymentConcepts = [...baseConcepts, ...existingAdditionalPayments]

      // Actualizar el estado de los conceptos basado en el progreso
      this.updateConceptsBasedOnProgress()

      console.log('Final paymentConcepts:', this.paymentConcepts)

      this.selectAll = false
    },

    toggleSelectAll() {
      this.paymentConcepts.forEach(concept => {
        if (!concept.disabled && !concept.isPaid) {
          concept.selected = this.selectAll
          // Si se está seleccionando, establecer el monto disponible
          if (this.selectAll) {
            const availableAmount = this.getAvailableAmountForConcept(concept.productKey, concept)
            if (availableAmount > 0) {
              concept.amount = availableAmount
            }
          }
        }
      })
      this.updateSelection()
    },

    updateSelection() {
      const availableConcepts = this.paymentConcepts.filter(c => !c.disabled && !c.isPaid)
      const selectedAvailable = availableConcepts.filter(c => c.selected)
      this.selectAll = availableConcepts.length > 0 && selectedAvailable.length === availableConcepts.length

      // Actualizar montos de conceptos seleccionados
      this.paymentConcepts.forEach(concept => {
        if (concept.selected && !concept.disabled && !concept.isPaid) {
          const availableAmount = this.getAvailableAmountForConcept(concept.productKey, concept)
          if (availableAmount > 0) {
            concept.amount = availableAmount
          }
        }
      })
    },

    async loadConceptProgress() {
      try {
        const contractId = this.contractInfo.Id || this.contractInfo.contractId
        if (!contractId) return

        const response = await axios.get(`${API_URL}/financing/concept-progress/${contractId}`)

        if (response.data.success) {
          this.conceptProgress = response.data.data
          console.log('Concept progress loaded:', this.conceptProgress)
        }
      } catch (error) {
        console.error('Error cargando progreso de conceptos:', error)
      }
    },

    updateConceptsBasedOnProgress() {
      // Actualizar el estado de los conceptos basado en el progreso cargado
      this.paymentConcepts.forEach(concept => {
        if (concept.productKey && this.conceptProgress[concept.productKey]) {
          const progress = this.conceptProgress[concept.productKey]
          const isPaid = progress.percentage >= 100
          concept.disabled = isPaid
          concept.isPaid = isPaid
        }
      })
    },

    async checkIfConceptPaid(productKey) {
      try {
        const contractId = this.contractInfo.Id || this.contractInfo.contractId
        if (!contractId) return false

        // Usar el progreso calculado en lugar de verificar pagos individuales
        const response = await axios.get(`${API_URL}/financing/concept-progress/${contractId}`)

        if (!response.data.success) return false

        const progress = response.data.data
        const conceptProgress = progress[productKey]

        // Un concepto está pagado solo si está al 100%
        return conceptProgress && conceptProgress.percentage >= 100
      } catch (error) {
        console.error(`Error verificando si concepto ${productKey} está pagado:`, error)
        return false
      }
    },

    // Métodos para manejo de contactos
    addSelectedMember() {
      if (!this.selectedMemberId) return

      const member = this.allContractMembers.find(m => m._id === this.selectedMemberId)
      if (!member) return

      // Agregar email si existe y no está duplicado
      if (member.Email && !this.selectedContacts.emails.includes(member.Email)) {
        this.selectedContacts.emails.push(member.Email)
      }

      // Agregar teléfono si existe y no está duplicado
      if (member.Telefono && !this.selectedContacts.phones.includes(member.Telefono)) {
        this.selectedContacts.phones.push(member.Telefono)
      }

      // Limpiar selección
      this.selectedMemberId = ''
    },

    addManualEmail() {
      if (this.newEmail && this.isValidEmail(this.newEmail) && !this.selectedContacts.emails.includes(this.newEmail)) {
        this.selectedContacts.emails.push(this.newEmail)
        this.newEmail = ''
      }
    },

    removeEmail(index) {
      this.selectedContacts.emails.splice(index, 1)
    },

    addManualPhone() {
      if (this.newPhone && !this.selectedContacts.phones.includes(this.newPhone)) {
        this.selectedContacts.phones.push(this.newPhone)
        this.newPhone = ''
      }
    },

    removePhone(index) {
      this.selectedContacts.phones.splice(index, 1)
    },

    isValidEmail(email) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
      return emailRegex.test(email)
    },

    showNextStepTooltip() {
      if (this.currentStep === 1 && !this.canProceedToNextStep && this.selectedConceptsCount > 0) {
        this.showTooltip = true
      }
    },

    hideNextStepTooltip() {
      this.showTooltip = false
    },

    resetContacts() {
      this.selectedContacts = {
        emails: [],
        phones: []
      }
      this.selectedMemberId = ''
      this.newEmail = ''
      this.newPhone = ''
    },

    // Métodos de navegación
    nextStep() {
      if (this.canProceedToNextStep) {
        this.currentStep++
      }
    },

    previousStep() {
      if (this.currentStep > 1) {
        this.currentStep--
      }
    },

    closeModal() {
      this.isModalActive = false
      this.currentStep = 1
      this.resetContacts()
      this.$emit('close')
    },

    canSelectConcept(conceptKey) {
      // Verificar si el concepto está completamente pagado usando el progreso real
      const concept = this.conceptProgress[conceptKey]
      if (concept && concept.percentage >= 100) {
        return false
      }

      return true
    },

    getMaxAmountForConcept(conceptKey) {
      const concept = this.conceptProgress[conceptKey]
      if (concept && concept.total > 0) {
        const remaining = (concept.total - concept.paid) / 100 // Convertir de centavos a dólares
        return remaining > 0 ? remaining : 0
      }

      // Si no hay progreso registrado, usar los montos totales del contrato
      switch (conceptKey) {
        case 'downPayment':
          return this.detailsContract?.PagoInicial || 0
        case 'adminCost':
          return this.detailsContract?.CostoAdministracion || 0
        case 'additionalPayment':
          // Para additionalPayment, solo retornar 0 ya que no debe ser un concepto genérico
          return 0
        default:
          return 0
      }
    },

    getAvailableAmountForConcept(conceptKey, concept = null) {
      // Si es un concepto específico (pago adicional individual), usar su monto original
      if (concept && concept.isExistingPayment && concept.originalPayment) {
        const amount = typeof concept.originalPayment.CargoPago === 'string' ?
          parseFloat(concept.originalPayment.CargoPago) || 0 :
          (parseFloat(concept.originalPayment.CargoPago) || 0)
        return amount
      }

      const conceptProgress = this.conceptProgress[conceptKey]
      if (conceptProgress && conceptProgress.total > 0) {
        const remaining = (conceptProgress.total - conceptProgress.paid) / 100 // Convertir de centavos a dólares
        return remaining > 0 ? remaining : 0
      }

      // Si no hay progreso, el monto disponible es el total del contrato
      switch (conceptKey) {
        case 'downPayment':
          return this.detailsContract?.PagoInicial || 0
        case 'adminCost':
          return this.detailsContract?.CostoAdministracion || 0
        case 'additionalPayment':
          // Para conceptos genéricos de additionalPayment, sumar todos los ADP y CDP
          let total = 0
          if (this.additionalPayments && this.additionalPayments.length > 0) {
            total += this.additionalPayments.reduce((sum, payment) => {
              const amount = typeof payment.CargoPago === 'string' ?
                parseFloat(payment.CargoPago) || 0 :
                (parseFloat(payment.CargoPago) || 0)
              return sum + amount
            }, 0)
          }
          if (this.conditionalDownPayment && this.conditionalDownPayment.length > 0) {
            total += this.conditionalDownPayment.reduce((sum, payment) => {
              const amount = typeof payment.CargoPago === 'string' ?
                parseFloat(payment.CargoPago) || 0 :
                (parseFloat(payment.CargoPago) || 0)
              return sum + amount
            }, 0)
          }
          return total
        default:
          return 0
      }
    },

    validateConceptAmount(concept) {
      const maxAmount = this.getAvailableAmountForConcept(concept.productKey, concept)

      // Validar que no sea negativo
      if (concept.amount < 0) {
        concept.amount = 0
        this.$buefy.toast.open({
          message: 'El monto no puede ser negativo',
          type: 'is-warning',
          duration: 3000
        })
        return
      }

      // Validar que no exceda el máximo
      if (maxAmount !== null && concept.amount > maxAmount) {
        concept.amount = maxAmount
        this.$buefy.toast.open({
          message: `El monto no puede exceder ${this.formatCurrency(maxAmount)}`,
          type: 'is-warning',
          duration: 3000
        })
        return
      }

      // Validar que sea mayor a 0 si está seleccionado
      if (concept.selected && concept.amount <= 0) {
        this.$buefy.toast.open({
          message: 'El monto debe ser mayor a 0',
          type: 'is-warning',
          duration: 3000
        })
        return
      }
    },

    isAmountInvalid(concept) {
      if (!concept.selected || concept.disabled) return false

      // Verificar que el monto sea mayor a 0
      if (concept.amount <= 0) return true

      // Verificar que no exceda el máximo permitido
      const maxAmount = this.getAvailableAmountForConcept(concept.productKey, concept)
      if (maxAmount !== null && concept.amount > maxAmount) return true

      return false
    },

    async confirmSelection() {
      if (!this.isValidForFinalSubmission) return

      this.creatingCheckoutSession = true

      try {
        const selectedConcepts = this.paymentConcepts
          .filter(concept => concept.selected && !concept.disabled)
          .map(concept => ({
            productKey: concept.productKey,
            amount: parseFloat(concept.amount),
            isExistingPayment: concept.isExistingPayment || false,
            paymentId: concept.paymentId || null,
            originalPayment: concept.originalPayment || null
          }))

        const payload = {
          contractInfo: {
            contractId: this.contractInfo.Id || this.contractInfo.contractId,
            contractNumber: this.contractInfo.Numero || this.contractInfo.contractNumber,
          },
          selectedConcepts: selectedConcepts,
          contacts: {
            emails: this.selectedContacts.emails,
            phones: this.selectedContacts.phones
          },
          language: this.currentLanguage,
          userAgent: navigator.userAgent,
          ipAddress: 'N/A',
          includeSubscription: this.includeSubscription,
          subscriptionDetails: this.includeSubscription ? {
            amount: parseFloat(this.monthlyPayment.toFixed(2)),
            interval: 'month',
            isImmediate: true,
            trialDays: 0,
            totalAmount: this.detailsContract?.Saldo || 0,
            months: this.monthsToFinance,
            startDate: this.subscriptionStartDate ? this.subscriptionStartDate.toISOString() : undefined
          } : undefined
        }

        const response = await axios.post(`${API_URL}/financing/checkout-session`, payload)

        if (response.data.success) {
          console.log('✅ Checkout session creado exitosamente, emitiendo evento...')

          const eventPayload = {
            ...response.data.data,
            selectedConcepts: selectedConcepts.map(sc => {
              const concept = this.paymentConcepts.find(pc => pc.productKey === sc.productKey)
              return {
                ...sc,
                name: concept.name,
                description: concept.description
              }
            }),
            contacts: this.selectedContacts
          }

          console.log('📤 Emitiendo evento checkout-session-created con payload:', eventPayload)
          this.$emit('checkout-session-created', eventPayload)

          console.log('⏳ Esperando un momento antes de cerrar modal...')
          await this.$nextTick()
          setTimeout(() => {
            console.log('🚪 Cerrando modal...')
            this.closeModal()
          }, 100)
        } else {
          this.$buefy.toast.open({
            message: response.data.error || 'Error creando sesión de pago',
            type: 'is-danger',
            duration: 5000
          })
        }
      } catch (error) {
        console.error('Error creando Checkout Session:', error)
        this.$buefy.toast.open({
          message: (error.response && error.response.data && error.response.data.error) || 'Error de conexión al crear sesión de pago',
          type: 'is-danger',
          duration: 5000
        })
      } finally {
        this.creatingCheckoutSession = false
      }
    },

    async createSubscription() {
      if (!this.isValidForSubscription) return

      this.creatingSubscription = true

      try {
        const saldoToFinance = this.detailsContract?.Saldo || 0
        const payload = {
          contractInfo: {
            contractId: this.contractInfo.Id || this.contractInfo.contractId,
            contractNumber: this.contractInfo.Numero || this.contractInfo.contractNumber,
          },
          subscriptionDetails: {
            amount: parseFloat(this.monthlyPayment.toFixed(2)),
            interval: 'month',
            isImmediate: true,
            trialDays: 0,
            totalAmount: saldoToFinance,
            months: this.monthsToFinance,
            startDate: this.subscriptionStartDate ? this.subscriptionStartDate.toISOString() : undefined
          },
          language: this.currentLanguage
        }

        const response = await axios.post(`${API_URL}/financing/subscription`, payload)

        if (response.data.success) {
          this.$emit('subscription-created', {
            ...response.data.data,
            subscriptionDetails: payload.subscriptionDetails
          })

          this.closeModal()
        } else {
          this.$buefy.toast.open({
            message: response.data.error || 'Error creating subscription',
            type: 'is-danger',
            duration: 5000
          })
        }
      } catch (error) {
        console.error('Error creating subscription:', error)
        this.$buefy.toast.open({
          message: (error.response && error.response.data && error.response.data.error) || 'Connection error creating subscription',
          type: 'is-danger',
          duration: 5000
        })
      } finally {
        this.creatingSubscription = false
      }
    }
  }
}
</script>

<style scoped>
/* =========================================================
   Design tokens — scoped to this modal
   ========================================================= */
.modal-card {
  --fps-blue:         oklch(52% 0.18 250);
  --fps-blue-light:   oklch(95% 0.04 250);
  --fps-blue-border:  oklch(88% 0.06 250);
  --fps-green:        oklch(63% 0.15 148);
  --fps-green-light:  oklch(95% 0.04 148);
  --fps-red:          oklch(55% 0.20 25);
  --fps-red-light:    oklch(96% 0.04 25);
  --fps-red-border:   oklch(84% 0.07 25);

  --fps-surface:      oklch(99% 0.005 250);
  --fps-surface-alt:  oklch(97% 0.007 250);
  --fps-border:       oklch(89% 0.010 250);
  --fps-border-strong: oklch(79% 0.015 250);

  --fps-text:         oklch(22% 0.015 250);
  --fps-text-sec:     oklch(48% 0.015 250);
  --fps-text-muted:   oklch(65% 0.010 250);
  --fps-text-inverse: oklch(99% 0.005 250);

  margin: 0;
  max-height: calc(100vh - 20px);
  overflow: visible;
  background: var(--fps-surface);
}

.modal-card-body {
  max-height: calc(100vh - 200px);
  overflow-y: auto;
  position: relative;
  background: var(--fps-surface);
  padding: 1.25rem 1.5rem;
}

/* =========================================================
   Steps
   ========================================================= */
.fps-steps-container {
  padding-bottom: 1.25rem;
  border-bottom: 1px solid var(--fps-border);
}

.fps-steps {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  list-style: none;
  margin: 0;
  padding: 0;
}

.fps-step-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  flex: 1;
  position: relative;
}

.fps-step-item:not(:last-child)::after {
  content: '';
  position: absolute;
  top: 16px;
  left: 50%;
  width: 100%;
  height: 2px;
  background: var(--fps-border-strong);
  z-index: 0;
  transition: background 0.25s ease;
}

.fps-step-item.is-completed:not(:last-child)::after {
  background: var(--fps-green);
}

.fps-step-marker {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: var(--fps-border-strong);
  color: var(--fps-text-inverse);
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 13px;
  position: relative;
  z-index: 1;
  transition: background 0.25s ease, box-shadow 0.25s ease;
}

.fps-step-item.is-active .fps-step-marker {
  background: var(--fps-blue);
  box-shadow: 0 0 0 4px var(--fps-blue-light);
}

.fps-step-item.is-completed .fps-step-marker {
  background: var(--fps-green);
}

.fps-step-label {
  margin-top: 8px;
  font-size: 11px;
  font-weight: 600;
  color: var(--fps-text-muted);
  text-align: center;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.fps-step-item.is-active .fps-step-label {
  color: var(--fps-blue);
}

.fps-step-item.is-completed .fps-step-label {
  color: var(--fps-green);
}

/* =========================================================
   Loading state
   ========================================================= */
.fps-loading {
  text-align: center;
  padding: 3rem 0;
  color: var(--fps-text-sec);
}

/* =========================================================
   Concepts table
   ========================================================= */
.fps-table-wrapper {
  border: 1px solid var(--fps-border);
  border-radius: 6px;
  overflow: hidden;
  max-height: 360px;
  overflow-y: auto;
}

.fps-concepts-table thead th {
  position: sticky;
  top: 0;
  background: var(--fps-surface-alt);
  z-index: 1;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: var(--fps-text-sec);
  padding: 10px 12px;
  border-bottom: 1px solid var(--fps-border);
}

.fps-row-disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.fps-row-selected td {
  background: var(--fps-blue-light);
}

.fps-concept-name {
  font-weight: 600;
  color: var(--fps-text);
  margin-right: 6px;
}

.fps-paid-badge {
  font-size: 11px;
  color: var(--fps-green);
  vertical-align: middle;
}

.fps-text-secondary {
  font-size: 0.875rem;
  color: var(--fps-text-sec);
}

.fps-amount-field {
  min-width: 110px;
}

.fps-amount-hint {
  font-size: 0.72rem;
  margin-top: 3px;
  line-height: 1.3;
}

.fps-hint-neutral {
  color: var(--fps-text-muted);
}

.fps-hint-error {
  color: var(--fps-red);
}

.fps-amount-display {
  font-weight: 600;
  color: var(--fps-text);
  font-variant-numeric: tabular-nums;
}

/* =========================================================
   Selection summary strip
   ========================================================= */
.fps-selection-summary {
  display: flex;
  align-items: stretch;
  margin-top: 10px;
  border: 1px solid var(--fps-border);
  border-radius: 6px;
  overflow: hidden;
  background: var(--fps-surface-alt);
}

.fps-summary-item {
  display: flex;
  flex-direction: column;
  padding: 10px 16px;
  flex: 1;
  gap: 2px;
}

.fps-summary-divider {
  width: 1px;
  background: var(--fps-border);
  flex-shrink: 0;
}

.fps-summary-label {
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: var(--fps-text-muted);
}

.fps-summary-value {
  font-size: 1.15rem;
  font-weight: 700;
  color: var(--fps-text);
  font-variant-numeric: tabular-nums;
}

.fps-summary-total {
  color: var(--fps-green);
  font-size: 1.35rem;
}

/* =========================================================
   Invalid amounts warning
   ========================================================= */
.fps-amounts-warning {
  display: flex;
  align-items: center;
  gap: 8px;
  background: var(--fps-red-light);
  border: 1px solid var(--fps-red-border);
  border-radius: 6px;
  padding: 10px 14px;
  margin-top: 10px;
  font-size: 0.875rem;
  color: var(--fps-red);
  font-weight: 500;
}

/* =========================================================
   Subscription section
   ========================================================= */
.fps-subscription {
  border: 1px solid var(--fps-blue-border);
  border-radius: 6px;
  overflow: hidden;
}

.fps-subscription-header {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 9px 14px;
  background: var(--fps-blue-light);
  color: var(--fps-blue);
  font-weight: 700;
  font-size: 0.8rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  border-bottom: 1px solid var(--fps-blue-border);
}

.fps-sub-stats {
  display: flex;
  align-items: stretch;
  background: var(--fps-surface);
  border-bottom: 1px solid var(--fps-border);
}

.fps-stat-item {
  display: flex;
  flex-direction: column;
  padding: 14px 16px;
  gap: 4px;
}

.fps-stat-sep {
  background: var(--fps-border);
  width: 1px;
  flex-shrink: 0;
}

.fps-stat-label {
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: var(--fps-text-muted);
}

.fps-stat-value {
  font-size: 1.05rem;
  font-weight: 700;
  color: var(--fps-text);
  font-variant-numeric: tabular-nums;
}

.fps-stat-accent {
  color: var(--fps-blue);
  font-size: 1.2rem;
}

.fps-sub-action-row {
  display: flex;
  align-items: flex-end;
  flex-wrap: wrap;
  gap: 12px;
  padding: 12px 16px;
  background: var(--fps-surface-alt);
}

.fps-field-tight {
  margin-bottom: 0 !important;
}

.fps-reason {
  font-size: 0.72rem;
  color: var(--fps-text-muted);
  line-height: 1.3;
  text-align: center;
}

.fps-sub-note {
  font-size: 0.72rem;
  color: var(--fps-text-sec);
  line-height: 1.3;
}

/* =========================================================
   Contacts — step 2 and step 3
   ========================================================= */
.fps-members-selector {
  background: var(--fps-surface-alt);
  border: 1px solid var(--fps-border);
  border-radius: 6px;
  padding: 14px 16px;
}

.fps-section-title {
  font-size: 0.75rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: var(--fps-text-sec);
  margin-bottom: 10px;
}

.fps-contacts-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 14px;
}

.fps-contact-group {
  border: 1px solid var(--fps-border);
  border-radius: 6px;
  overflow: hidden;
}

.fps-contact-group-title {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 9px 13px;
  background: var(--fps-surface-alt);
  border-bottom: 1px solid var(--fps-border);
  font-size: 0.75rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: var(--fps-text-sec);
  margin: 0;
}

.fps-count-badge {
  margin-left: auto;
  background: var(--fps-blue);
  color: var(--fps-text-inverse);
  font-size: 10px;
  font-weight: 700;
  padding: 1px 7px;
  border-radius: 99px;
  min-width: 20px;
  text-align: center;
}

.fps-contact-group-body {
  padding: 12px;
}

.fps-chip-list {
  list-style: none;
  margin: 0;
  padding: 8px;
  display: flex;
  flex-direction: column;
  gap: 5px;
  max-height: 180px;
  overflow-y: auto;
}

.fps-chip-list-readonly {
  padding: 8px;
}

.fps-chip {
  display: flex;
  align-items: center;
  gap: 8px;
  background: var(--fps-surface);
  border: 1px solid var(--fps-border);
  border-radius: 4px;
  padding: 6px 10px;
  font-size: 0.85rem;
}

.fps-chip-readonly {
  background: var(--fps-surface-alt);
}

.fps-chip-icon {
  color: var(--fps-text-muted);
  font-size: 11px;
  flex-shrink: 0;
}

.fps-chip-text {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: var(--fps-text);
}

.fps-chip-remove {
  appearance: none;
  background: none;
  border: none;
  cursor: pointer;
  padding: 2px 5px;
  border-radius: 3px;
  color: var(--fps-text-muted);
  line-height: 1;
  flex-shrink: 0;
  transition: color 0.15s ease, background 0.15s ease;
}

.fps-chip-remove:hover {
  color: var(--fps-red);
  background: var(--fps-red-light);
}

.fps-chip-remove:focus-visible {
  outline: 2px solid var(--fps-blue);
  outline-offset: 1px;
}

.fps-chip-empty {
  text-align: center;
  padding: 0.75rem;
  color: var(--fps-text-muted);
  font-size: 0.85rem;
  font-style: italic;
}

/* =========================================================
   Review — step 3
   ========================================================= */
.fps-review-section {
  border: 1px solid var(--fps-border);
  border-radius: 6px;
  overflow: hidden;
}

.fps-review-section-title {
  padding: 9px 13px;
  background: var(--fps-surface-alt);
  border-bottom: 1px solid var(--fps-border);
  font-size: 0.75rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: var(--fps-text-sec);
  margin: 0;
}

.fps-review-table {
  margin-bottom: 0;
}

.fps-amount-col {
  text-align: right;
  font-variant-numeric: tabular-nums;
}

.fps-total-row {
  background: var(--fps-surface-alt);
}

.fps-total-row th {
  font-size: 0.875rem;
  color: var(--fps-text-sec);
}

.fps-total-cell {
  font-weight: 700;
  color: var(--fps-green);
  font-size: 1rem;
}

/* =========================================================
   Footer
   ========================================================= */
.fps-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
}

.fps-footer-left {
  display: flex;
  gap: 8px;
}

.fps-footer-right {
  display: flex;
  gap: 8px;
}

/* =========================================================
   Utilities
   ========================================================= */
.fps-sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}

/* =========================================================
   Focus — global override inside modal
   ========================================================= */
.modal-card :focus-visible {
  outline: 2px solid var(--fps-blue);
  outline-offset: 2px;
  border-radius: 2px;
}

/* =========================================================
   Compact field label
   ========================================================= */
.fps-label-compact {
  font-size: 0.7rem !important;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

/* =========================================================
   Responsive
   ========================================================= */
@media (max-width: 768px) {
  .fps-sub-stats {
    flex-direction: column;
  }

  .fps-stat-sep {
    width: 100%;
    height: 1px;
  }

  .fps-contacts-grid {
    grid-template-columns: 1fr;
  }

  .fps-steps {
    gap: 4px;
  }

  .fps-step-label {
    display: none;
  }

  .fps-step-item:not(:last-child)::after {
    top: 15px;
  }
}
</style>
